// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface VideoProps {
    src: string;
    autoPlay?: boolean;
    insideModal?: boolean;
}

type Props = VideoProps;

export interface VideoStyles {
    wrapper: InjectableCss;
}

export const videoStyles: VideoStyles = {
    wrapper: {
        css: css`
            ${({ insideModal }: any) =>
                insideModal
                    ? "position: relative;width: 100%;overflow: hidden;padding-top: 56.25%; /* 16:9 Aspect Ratio */"
                    : ""}
            iframe {
                ${({ insideModal }: any) =>
                    insideModal
                        ? "position: absolute;top: 0;left: 0;bottom: 0;right: 0;width: 100%;height: 100%;border: none;"
                        : "border-width: 0;"}
            }
        `,
    },
};

const styles = videoStyles;

const YoutubeVideo: React.FC<Props> = ({ src, autoPlay, insideModal = false }) => {
    const [srcUrl, setSrcUrl] = React.useState<string>("");

    React.useEffect(() => {
        const videoSrc = getNormalizedVideoSrc(src);
        setSrcUrl(videoSrc);
    }, [src]);

    const applyAutoPlay = (src: string) => {
        if (!autoPlay) {
            return src;
        }
        const autoPlayString = "autoplay=1";
        const hasQueryString = src && src.includes("?");
        const operand = hasQueryString ? "&" : "?";

        return src.concat(`${operand}${autoPlayString}`);
    };

    const extractVideoId = (src: string) => {
        if (!src) {
            return;
        }

        const normalVideoParts = src.match(/(\?|&)v=([^&#]+)/);
        const embedVideoParts = src.match(/(\embed\/)+([^\/]+)/);
        const dotBeParts = src.match(/(\.be\/)+([^\/]+)/);

        if (normalVideoParts) {
            return normalVideoParts.pop();
        }

        if (dotBeParts) {
            return dotBeParts.pop();
        }

        if (embedVideoParts) {
            return embedVideoParts.pop()?.replace("?rel=0", "");
        }

        return src;
    };

    const getNormalizedVideoSrc = (src: string) => {
        const videoId = extractVideoId(src);
        const videoSrc = `https://www.youtube.com/embed/${videoId}?feature=oembed&enablejsapi=1`;
        return applyAutoPlay(videoSrc);
    };

    if (!srcUrl) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...{ insideModal }}>
            <iframe
                width="100%"
                height="100%"
                data-src={srcUrl}
                className="lazyload"
                allow="autoplay;"
                allowFullScreen={true}
            />
        </StyledWrapper>
    );
};

export default YoutubeVideo;
